import {
  PLAYER_H, PLAYER_W, TICK_RATE, WORLD_H,
  type HazardDef, type LevelDef, type Platform, type PlayerState, type Point, type Rect, type WorldState,
} from '../../shared/types';
import { PHYSICS, getPlatforms, isHazardActive, overlaps } from '../../shared/simulation';
import type { CameraView } from './camera';
import { ENAMEL_COLORS } from './lantern';
import { INK, PAPER, TAU, clamp, disc, rounded, spark, text } from './paint';

/** The whole "fall" beat: splash, centred chip, fade-in and shake all end together. */
export const RESET_BEAT_MS = 1100;
/** Couriers fade in at spawn instead of snapping. */
export const RESET_FADE_MS = 250;
/** The fallen courier's badge shakes briefly. */
export const RESET_SHAKE_MS = 420;
/**
 * How many authoritative ticks the last received snapshot may precede the deadly tick: one 20 Hz
 * broadcast interval (3 ticks), one more interval in case the shell coalesced two snapshots into
 * a single render, and rounding. A body walks ≤ 29 px or falls ≤ 101 px in that time.
 */
export const RESET_LOOKAHEAD_TICKS = 8;

const STEP = 1 / TICK_RATE;
const EPS = 1e-7;

export type FallSurface = 'water' | 'beam' | 'bramble' | 'bottom';

/**
 * Who a received `reset` event was about. A requested retry is authoritative: since 0.9.0 the engine's
 * event carries `playerId` when a courier pressed R (shared/simulation.ts resetWorld(world, level,
 * requestedBy)), and that courier is named (`basis: 'requested'`). Falls are inferred from the last
 * snapshot received BEFORE the reset (the fall event itself is anonymous). `basis` says how: an actual
 * overlap with an active hazard in that snapshot, feet already below the world, or a short kinematic
 * extrapolation (the body would have crossed within the snapshot interval). An anonymous `retry`
 * (no candidate, no requester) is, rarely, a reset the engine performed to resolve a crush/rejoin.
 */
export interface ResetAttribution {
  kind: 'fall' | 'retry';
  playerId: string | null;
  name: string | null;
  color: number | null;
  /** World-space location of the fall: the water surface where the body went in, the body's
   * centre inside a beam/bramble field, or the point where it left the world. */
  point: Point | null;
  surface: FallSurface | null;
  hazardId: string | null;
  basis: 'overlap' | 'below' | 'extrapolated' | 'requested' | 'none';
  ticksAhead: number;
}

export interface ResetBeat {
  at: number;
  eventId: number;
  attribution: ResetAttribution;
  /** The sentence after the name pill ("fell in — everyone back!"), or the whole label for a retry. */
  sentence: string;
}

const RETRY: ResetAttribution = { kind: 'retry', playerId: null, name: null, color: null, point: null, surface: null, hazardId: null, basis: 'none', ticksAhead: 0 };

const overlapX = (a: Rect, b: Rect) => Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
const overlapArea = (a: Rect, b: Rect) => Math.max(0, overlapX(a, b)) * Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));

/** Mirrors the engine's swept test: the horizontal and vertical sweeps of one tick's motion. */
function crossed(from: Rect, to: Rect, target: Rect): boolean {
  const horizontal: Rect = { x: Math.min(from.x, to.x), y: from.y, w: from.w + Math.abs(to.x - from.x), h: from.h };
  const vertical: Rect = { x: to.x, y: Math.min(from.y, to.y), w: to.w, h: to.h + Math.abs(to.y - from.y) };
  return overlaps(horizontal, target) || overlaps(vertical, target);
}

function contactPoint(rect: Rect, hazard: HazardDef): Point {
  const x = clamp(rect.x + rect.w / 2, hazard.x + 6, hazard.x + hazard.w - 6);
  if (hazard.kind === 'water') return { x, y: hazard.y };
  return { x, y: clamp(rect.y + rect.h / 2, hazard.y + 8, hazard.y + hazard.h - 8) };
}

interface Contact { ticks: number; hazard: HazardDef | null; rect: Rect; depth: number }

/**
 * Where a body would be over the next few ticks, from its authoritative velocity: walking
 * continues (a solid side stops it), gravity resumes off an edge, a platform top catches a fall.
 * Support by another courier keeps it standing. Semantic state never advances here.
 */
function extrapolate(player: PlayerState, level: LevelDef, previous: WorldState, lookahead: number): Contact | null {
  const w = PLAYER_W, h = PLAYER_H;
  let { x, y, vy, grounded } = player;
  const vx = player.vx;
  const onBody = !!player.supportId && previous.players.some(other => other.id === player.supportId && other.id !== player.id);
  for (let k = 1; k <= lookahead; k++) {
    const clock: WorldState = { ...previous, elapsed: previous.elapsed + k * STEP };
    const platforms: Platform[] = getPlatforms(level, clock);
    const before: Rect = { x, y, w, h };
    let nx = x + vx * STEP;
    if (platforms.some(p => overlaps({ x: nx, y, w, h }, p))) nx = x;
    let nvy = grounded ? 0 : Math.min(PHYSICS.maxFall, vy + PHYSICS.gravity * STEP);
    let ny = grounded ? y : y + nvy * STEP;
    if (!grounded && nvy > 0) {
      const landing = platforms
        .filter(p => overlapX({ x: nx, y, w, h }, p) > EPS && y + h <= p.y + 1e-4 && ny + h >= p.y)
        .sort((a, b) => a.y - b.y)[0];
      if (landing) { ny = landing.y - h; nvy = 0; grounded = true; }
    } else if (grounded && !onBody) {
      const supported = platforms.some(p => overlapX({ x: nx, y, w, h }, p) > EPS && Math.abs(p.y - (y + h)) < 1e-3);
      if (!supported) grounded = false;
    }
    x = nx; y = ny; vy = nvy;
    const after: Rect = { x, y, w, h };
    let hit: Contact | null = null;
    for (const hazard of level.hazards) {
      if (!isHazardActive(hazard, clock) || !crossed(before, after, hazard)) continue;
      const depth = overlapArea(after, hazard);
      if (!hit || depth > hit.depth) hit = { ticks: k, hazard, rect: after, depth };
    }
    if (hit) return hit;
    if (y + h > WORLD_H) return { ticks: k, hazard: null, rect: after, depth: y + h - WORLD_H };
  }
  return null;
}

/**
 * Attribute a received `reset` to the courier who fell, from the last pre-reset snapshot.
 * Never labels a reset a retry while any body overlapped an active hazard in that snapshot.
 */
export function attributeReset(previous: WorldState, level: LevelDef, lookahead = RESET_LOOKAHEAD_TICKS): ResetAttribution {
  const bodies = previous.players.filter(p => p.connected && !p.entered);
  let best: { player: PlayerState; contact: Contact; basis: ResetAttribution['basis'] } | null = null;
  const better = (candidate: { contact: Contact }) =>
    !best || candidate.contact.ticks < best.contact.ticks || (candidate.contact.ticks === best.contact.ticks && candidate.contact.depth > best.contact.depth);
  for (const player of bodies) {
    const rect: Rect = { x: player.x, y: player.y, w: PLAYER_W, h: PLAYER_H };
    let direct: Contact | null = null;
    for (const hazard of level.hazards) {
      if (!isHazardActive(hazard, previous) || !overlaps(rect, hazard)) continue;
      const depth = overlapArea(rect, hazard);
      if (!direct || depth > direct.depth) direct = { ticks: 0, hazard, rect, depth };
    }
    if (direct) { if (better({ contact: direct })) best = { player, contact: direct, basis: 'overlap' }; continue; }
    if (rect.y + rect.h > WORLD_H) {
      const contact = { ticks: 0, hazard: null, rect, depth: rect.y + rect.h - WORLD_H };
      if (better({ contact })) best = { player, contact, basis: 'below' };
      continue;
    }
    const ahead = extrapolate(player, level, previous, lookahead);
    if (ahead && better({ contact: ahead })) best = { player, contact: ahead, basis: 'extrapolated' };
  }
  if (!best) return RETRY;
  const { player, contact, basis } = best;
  const hazard = contact.hazard;
  return {
    kind: 'fall', playerId: player.id, name: player.name, color: player.color,
    point: hazard ? contactPoint(contact.rect, hazard) : { x: contact.rect.x + PLAYER_W / 2, y: WORLD_H },
    surface: hazard ? hazard.kind : 'bottom', hazardId: hazard?.id ?? null, basis, ticksAhead: contact.ticks,
  };
}

/**
 * The courier who asked for the retry, from the event's authoritative `playerId`: the engine only
 * stamps a `reset` when a courier pressed R, so this outranks any fall inference. The name and colour
 * come from the roster of either world (the reset world keeps the same players); an unknown id
 * (a courier who left in the same instant) falls back to the anonymous retry.
 */
export function attributeRequestedReset(playerId: string | undefined, worlds: readonly (WorldState | null)[]): ResetAttribution | null {
  if (!playerId) return null;
  for (const world of worlds) {
    const player = world?.players.find(p => p.id === playerId);
    if (player) return { ...RETRY, playerId, name: player.name, color: player.color, basis: 'requested' };
  }
  return RETRY;
}

export function resetSentence(attribution: ResetAttribution): string {
  if (attribution.kind !== 'fall') return attribution.playerId ? 'asked for a retry — everyone back!' : 'Retry together!';
  switch (attribution.surface) {
    case 'water': return 'fell in — everyone back!';
    case 'beam': return 'got singed — everyone back!';
    case 'bramble': return 'hit the thorns — everyone back!';
    default: return 'fell — everyone back!';
  }
}

/** Whether the chip carries a name pill (a fall, or a retry with a known requester). */
export const namedReset = (attribution: ResetAttribution): boolean => !!attribution.playerId && attribution.name !== null;

/** The chip's full wording, for diagnostics and the accessible summary. */
export function resetLabel(beat: ResetBeat): string {
  const a = beat.attribution;
  return namedReset(a) ? `${a.name} ${beat.sentence}` : beat.sentence;
}

const ease = (n: number) => { const t = clamp(n, 0, 1); return t * t * (3 - 2 * t); };
const FONT = '"Trebuchet MS", "Avenir Next", system-ui, sans-serif';

export interface ResetChipLayout extends Rect {
  size: number;
  pill: Rect | null;
  textX: number;
}

/** A deterministic centred layout (CSS px) so other layers can avoid the chip before it is drawn. */
export function measureResetChip(ctx: CanvasRenderingContext2D, view: CameraView, beat: ResetBeat): ResetChipLayout {
  const a = beat.attribution;
  const phone = view.width < 500;
  const maxW = view.width - 16;
  let size = phone ? 15 : 19;
  ctx.save();
  let pillW = 0, sentenceW = 0, padX = 0, badge = 0, gap = 0;
  for (; size >= 11; size -= 1) {
    ctx.font = `800 ${size}px ${FONT}`;
    padX = Math.round(size * 0.95);
    badge = Math.round(size * 1.15);
    gap = Math.round(size * 0.45);
    pillW = namedReset(a) ? Math.round(ctx.measureText(a.name ?? '').width + badge + gap * 3) : 0;
    sentenceW = Math.round(ctx.measureText(beat.sentence).width);
    if (padX * 2 + pillW + (pillW ? gap : 0) + sentenceW <= maxW) break;
  }
  ctx.restore();
  const h = Math.round(size * 2.3);
  const w = Math.min(maxW, padX * 2 + pillW + (pillW ? gap : 0) + sentenceW);
  const x = Math.round((view.width - w) / 2);
  // Upper third of the view, but below the trail map's band (8..108 + gap) so the map keeps its slot.
  const y = Math.round(clamp(Math.max(view.height * 0.34 - h / 2, 116), 8, view.height - h - 8));
  const pill = pillW ? { x: x + padX, y: y + Math.round((h - badge - 8) / 2), w: pillW, h: badge + 8 } : null;
  return { x, y, w, h, size, pill, textX: x + padX + (pill ? pillW + gap : 0) };
}

/** The large centred chip: "[●2 Bo] fell in — everyone back!", "[●2 Bo] asked for a retry — everyone back!" or "Retry together!". */
export function drawResetChip(ctx: CanvasRenderingContext2D, layout: ResetChipLayout, beat: ResetBeat, now: number, reducedMotion: boolean): void {
  const age = now - beat.at;
  if (age < 0 || age >= RESET_BEAT_MS) return;
  const a = beat.attribution;
  const alpha = reducedMotion ? 1 : Math.min(1, 0.4 + age / 80) * ease((RESET_BEAT_MS - age) / 220);
  const pop = reducedMotion ? 1 : 0.94 + 0.06 * ease(age / 140);
  const cx = layout.x + layout.w / 2, cy = layout.y + layout.h / 2;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(cx, cy); ctx.scale(pop, pop); ctx.translate(-cx, -cy);
  ctx.lineJoin = 'round';
  rounded(ctx, layout.x, layout.y + 3, layout.w, layout.h, layout.h / 2);
  ctx.fillStyle = 'rgba(31,49,39,.2)'; ctx.fill();
  rounded(ctx, layout.x, layout.y, layout.w, layout.h, layout.h / 2);
  ctx.fillStyle = PAPER; ctx.fill();
  ctx.strokeStyle = INK; ctx.lineWidth = 2.2; ctx.stroke();
  if (namedReset(a) && layout.pill) {
    const pill = layout.pill;
    const badge = Math.round(layout.size * 1.15);
    const gap = Math.round(layout.size * 0.45);
    rounded(ctx, pill.x, pill.y, pill.w, pill.h, pill.h / 2);
    ctx.fillStyle = ENAMEL_COLORS[((a.color ?? 0) % 8 + 8) % 8]; ctx.fill();
    ctx.strokeStyle = INK; ctx.lineWidth = 1.6; ctx.stroke();
    const bx = pill.x + gap + badge / 2;
    disc(ctx, bx, cy, badge / 2, INK);
    text(ctx, String(((a.color ?? 0) % 8) + 1), bx, cy + 0.5, Math.round(layout.size * 0.78), PAPER, 'center', 900);
    text(ctx, a.name ?? '', pill.x + gap * 2 + badge, cy + 0.5, layout.size, INK, 'left', 800);
  }
  ctx.beginPath(); ctx.rect(layout.x, layout.y, layout.w, layout.h); ctx.clip();
  text(ctx, beat.sentence, layout.textX, cy + 0.5, layout.size, INK, 'left', 800);
  ctx.restore();
}

/**
 * The world-space mark where the courier fell, drawn under the bodies: rings on the water
 * surface, a starburst in a beam or bramble field, a dust arc at the world's edge. Animated over
 * the beat; a still mark under reduced motion (the location is information, the motion is not).
 */
export function drawFallMark(ctx: CanvasRenderingContext2D, beat: ResetBeat, now: number, reducedMotion: boolean): void {
  const a = beat.attribution;
  if (a.kind !== 'fall' || !a.point) return;
  const age = now - beat.at;
  if (age < 0 || age >= RESET_BEAT_MS) return;
  const t = reducedMotion ? 0.35 : clamp(age / RESET_BEAT_MS, 0, 1);
  const fade = reducedMotion ? 0.9 : (1 - t) * 0.95;
  const { x, y } = a.point;
  ctx.save();
  ctx.lineCap = 'round';
  if (a.surface === 'water') {
    const rings = [[0, '#f4fbff', 2.4], [0.12, '#dff3f2', 1.8], [0.26, '#b9dde0', 1.4]] as const;
    for (const [index, [delay, color, width]] of rings.entries()) {
      const k = reducedMotion ? [0.5, 0.32, 0.15][index] : clamp((t - delay) / (1 - delay), 0, 1);
      if (k <= 0) continue;
      ctx.globalAlpha = fade * (1 - k * 0.8);
      ctx.beginPath();
      ctx.ellipse(x, y + 1, 9 + k * 46, 3.5 + k * 12, 0, 0, TAU);
      ctx.strokeStyle = color; ctx.lineWidth = width; ctx.stroke();
    }
    // A pale crown of foam where the body went in.
    ctx.globalAlpha = fade * (reducedMotion ? 0.8 : Math.max(0, 1 - t * 1.6));
    ctx.beginPath();
    ctx.ellipse(x, y + 1, 12 + t * 10, 3 + t * 2, 0, 0, TAU);
    ctx.fillStyle = '#f7fdff'; ctx.fill();
  } else if (a.surface === 'beam' || a.surface === 'bramble') {
    const rays = a.surface === 'beam' ? 8 : 6;
    const radius = a.surface === 'beam' ? 14 + t * 30 : 12 + t * 22;
    ctx.globalAlpha = fade;
    for (let i = 0; i < rays; i++) {
      const angle = i / rays * TAU + (a.surface === 'beam' ? 0.2 : 0.55);
      const inner = radius * 0.45, outer = radius;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(angle) * inner, y + Math.sin(angle) * inner);
      ctx.lineTo(x + Math.cos(angle) * outer, y + Math.sin(angle) * outer);
      ctx.strokeStyle = a.surface === 'beam' ? (i % 2 ? '#ffd166' : '#ff9a3c') : (i % 2 ? '#e8dcb8' : '#a67c52');
      ctx.lineWidth = a.surface === 'beam' ? 2.6 - t * 1.4 : 2.2 - t;
      ctx.stroke();
    }
    ctx.globalAlpha = fade * (reducedMotion ? 0.85 : Math.max(0, 1 - t * 1.4));
    spark(ctx, x, y, 9 + t * 6, a.surface === 'beam' ? '#fff1b8' : '#f2e6c8');
    disc(ctx, x, y, 3.5, a.surface === 'beam' ? '#3b2a1c' : INK);
  } else {
    ctx.globalAlpha = fade;
    ctx.beginPath();
    ctx.ellipse(x, y - 4, 16 + t * 30, 6 + t * 8, 0, Math.PI, TAU);
    ctx.strokeStyle = '#c9bb92'; ctx.lineWidth = 3 - t * 1.5; ctx.stroke();
  }
  ctx.restore();
}
